import Glide, { Swipe } from '@glidejs/glide/dist/glide.modular.esm'

/**
 * History timeline: on desktop (>=1200px), clicking a year point on the line
 * swaps which year's card is shown and slides the card horizontally so it
 * always opens directly above the clicked point. Points and cards are
 * matched by position among siblings (not by an id/index attribute) — both
 * lists come from the same "years" ContentCollection in the same order,
 * same pattern as PipelineModule.js's tab nav / tab panel matching.
 *
 * Below 1200px the dot/line track doesn't have room to stay usable (labels
 * and dots start overlapping), so the point/track UI is hidden entirely
 * (see the "max-width: 1199px" block in HistoryTimeline.scss) and the same
 * cards become a swipeable glide.js slider instead (3 cards per slide from
 * 992-1199px, 2 from 576-991px, 1 below that), each showing its own year
 * label plus a small scrubber line summarising the visible card(s)' years.
 *
 * @return {void}
 */
export default function InitHistoryTimeline() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const mobileQuery = window.matchMedia('(max-width: 1199px)')

    document.querySelectorAll('[data-history-timeline]').forEach(function (module) {
        // editors need to see/edit every year's card at once — none of the
        // active/positioning/slider logic below applies in the backend (see
        // the ".neos-backend" overrides in HistoryTimeline.scss)
        if (window.neos || document.body.classList.contains('neos-backend')) {
            return
        }

        const timeline = module.querySelector('[data-history-timeline-timeline]')
        const cardsContainer = module.querySelector('[data-history-timeline-cards]')
        const points = Array.from(module.querySelectorAll('.history-timeline__point'))
        const cards = Array.from(module.querySelectorAll('.history-timeline__card'))
        const nextButton = module.querySelector('[data-history-timeline-next]')

        if (!timeline || !cardsContainer || !points.length || !cards.length) {
            return
        }

        let glide = null
        let activeIndex = 0
        let mode = null

        // ---- desktop: click-to-position floating card ----

        const positionCard = () => {
            const activeCard = cards[activeIndex]
            const point = points[activeIndex]
            const timelineRect = timeline.getBoundingClientRect()
            const pointRect = point.getBoundingClientRect()
            const pointCenter = pointRect.left + pointRect.width / 2 - timelineRect.left

            const cardWidth = activeCard.offsetWidth
            const maxLeft = Math.max(0, timelineRect.width - cardWidth)
            const left = Math.max(0, Math.min(maxLeft, pointCenter - cardWidth / 2))

            // a transform, not "left" — ".history-timeline__cards" is
            // normal-flow (see HistoryTimeline.scss), so this only shifts
            // it visually and never changes the height it reserves in the
            // page for whatever the active card's content needs
            cardsContainer.style.transform = `translateX(${left}px)`
            cardsContainer.style.setProperty('--history-timeline-arrow-left', `${pointCenter - left}px`)
        }

        const setActiveDesktop = (index) => {
            activeIndex = index

            points.forEach((point, i) => point.classList.toggle('is-active', i === index))

            cards.forEach((card, i) => {
                if (i === index) {
                    card.classList.add('is-active')
                } else {
                    card.classList.remove('is-active', 'is-visible')
                }
            })

            positionCard()

            // triggers the fade/scale-in transition on the newly active
            // card — has to happen a frame after ".is-active" switches it
            // to display:block, or the browser coalesces both class changes
            // into one style recalc and the transition never runs
            const activeCard = cards[index]
            if (reduceMotion) {
                activeCard.classList.add('is-visible')
            } else {
                requestAnimationFrame(() => requestAnimationFrame(() => activeCard.classList.add('is-visible')))
            }
        }

        // desktop-only — the points/track are hidden below 992px (see
        // HistoryTimeline.scss), so this is unreachable to click there;
        // attached once, unconditionally, since that's harmless either way
        points.forEach((point, index) => point.addEventListener('click', () => setActiveDesktop(index)))

        const initDesktop = () => {
            setActiveDesktop(activeIndex)
        }

        const teardownDesktop = () => {
            cardsContainer.style.transform = ''
            cardsContainer.style.removeProperty('--history-timeline-arrow-left')
        }

        // ---- mobile/tablet: glide.js card slider ----

        // Glide needs a "[data-glide-el=track] > (wrapper)" DOM structure
        // that doesn't exist in the desktop markup — built here on demand
        // instead of always being in the Fusion template, because
        // glide.core.scss's "overflow: hidden" on .glide__track would clip
        // the desktop card's little arrow tail (it sticks out 9px below the
        // card, see "&__card-arrow" in HistoryTimeline.scss) if that wrapper
        // were permanently around it
        const onNextClick = () => glide && glide.go('>')

        // phone (<576px): 1 card. Tablet (576-991px): 2. The 992-1199px
        // range has enough room for a 3rd, so it gets one too instead of
        // matching the (narrower) tablet tier, per explicit request.
        const getResponsiveSettings = () => {
            if (window.matchMedia('(max-width: 575px)').matches) {
                return { perView: 1, gap: 12 }
            }
            if (window.matchMedia('(max-width: 991px)').matches) {
                return { perView: 2, gap: 16 }
            }
            return { perView: 3, gap: 16 }
        }

        // Glide's own "breakpoints" option only re-evaluates on a real
        // resize event, not at initial mount — so a phone-width viewport
        // that never resizes (i.e. every real phone) kept the base perView
        // instead of the breakpoint's. Recomputing settings ourselves (same
        // pattern as ImageTextTileSlider.js) avoids that.
        const onGlideResize = () => glide && glide.update(getResponsiveSettings())

        const initGlide = () => {
            const track = document.createElement('div')
            track.className = 'glide__track'
            track.setAttribute('data-glide-el', 'track')

            const slidesWrap = document.createElement('div')
            slidesWrap.className = 'glide__slides history-timeline__cards-slides'

            cards.forEach((card) => {
                card.classList.add('glide__slide')
                slidesWrap.appendChild(card)
            })

            track.appendChild(slidesWrap)

            // mini scrubber line below the slider — shows a marker + year
            // label for exactly the card(s) currently in view (1 on mobile,
            // 2 on tablet, matching perView), rebuilt on every glide "run"
            // by buildScrubber() below
            const scrubber = document.createElement('div')
            scrubber.className = 'history-timeline__cards-scrubber'
            scrubber.innerHTML = `
                <div class="history-timeline__cards-scrubber-line"></div>
                <div class="history-timeline__cards-scrubber-markers"></div>
            `
            const scrubberMarkers = scrubber.querySelector('.history-timeline__cards-scrubber-markers')

            const buildScrubber = () => {
                const perView = glide.settings.perView
                scrubberMarkers.innerHTML = ''
                for (let i = 0; i < perView; i++) {
                    const card = cards[glide.index + i]
                    if (!card) {
                        continue
                    }
                    const year = card.querySelector('.history-timeline__card-year')
                    const marker = document.createElement('div')
                    marker.className = 'history-timeline__cards-scrubber-marker'
                    marker.innerHTML = `
                        <span class="history-timeline__cards-scrubber-dot"></span>
                        <span class="history-timeline__cards-scrubber-year">${year ? year.textContent : ''}</span>
                    `
                    scrubberMarkers.appendChild(marker)
                }
            }

            cardsContainer.appendChild(track)
            cardsContainer.appendChild(scrubber)
            cardsContainer.classList.add('glide')

            glide = new Glide(cardsContainer, {
                type: 'carousel',
                rewind: true,
                animationDuration: 420,
                animationTimingFunc: 'cubic-bezier(0.22, 1, 0.36, 1)',
                ...getResponsiveSettings(),
            })

            if (nextButton) {
                nextButton.addEventListener('click', onNextClick)
            }

            glide.on('run', buildScrubber)
            glide.on('update', buildScrubber)

            glide.mount({ Swipe })
            buildScrubber()

            window.addEventListener('resize', onGlideResize)
        }

        const teardownGlide = () => {
            if (glide) {
                glide.destroy()
                glide = null
            }

            window.removeEventListener('resize', onGlideResize)

            if (nextButton) {
                nextButton.removeEventListener('click', onNextClick)
            }

            // move the real cards back out to be direct children of
            // cardsContainer again (appendChild moves, doesn't clone) before
            // discarding the track — anything left inside the track at that
            // point is a clone Glide generated for looping, not one of ours
            cards.forEach((card) => {
                card.classList.remove('glide__slide', 'glide__slide--active', 'glide__slide--clone')
                cardsContainer.appendChild(card)
            })

            const track = cardsContainer.querySelector('[data-glide-el="track"]')
            if (track) {
                track.remove()
            }

            const scrubber = cardsContainer.querySelector('.history-timeline__cards-scrubber')
            if (scrubber) {
                scrubber.remove()
            }

            cardsContainer.classList.remove('glide')
        }

        // ---- mode switching ----

        const applyMode = () => {
            const nextMode = mobileQuery.matches ? 'mobile' : 'desktop'
            if (nextMode === mode) {
                return
            }
            mode = nextMode

            if (mode === 'mobile') {
                teardownDesktop()
                initGlide()
            } else {
                teardownGlide()
                initDesktop()
            }
        }

        applyMode()

        window.addEventListener('resize', () => {
            if (mode === 'desktop') {
                positionCard()
            }
        })

        mobileQuery.addEventListener('change', applyMode)
    })
}
